import { FlightData, LatLng } from '../types'
import L from 'leaflet'
import greatCircle from '@turf/great-circle'
import { point } from '@turf/helpers'

export class Path {
  map: L.Map
  path: L.Polyline
  pathGeo: L.GeoJSON

  constructor (map: L.Map) {
    this.map = map
    this.map.on('resize', this.onResize, this)
  }

  apply (flights: FlightData[]) {
    let coordinates: LatLng[] = []
    for (const flight of flights) {
      if (flight.departureAirport.code === flight.arrivalAirport.code) {
        const airportCoord = flight.departureAirport.coords || flight.arrivalAirport.coords
        coordinates = coordinates.concat({ lat: airportCoord.lat, lng: airportCoord.lng })
        continue
      }
      if (flights.length > 1 && flight.status.includes('Cancelled')) continue
      if (flight.route.length > 0) {
        coordinates = coordinates.concat(this.convertForAntiMeridian(flight.route))
      } else if (flight.departureAirport.coords && flight.arrivalAirport.coords) {
        coordinates = coordinates.concat(this.generateArcLine(flight.departureAirport.coords, flight.arrivalAirport.coords))
      }
    }
    if (this.path) {
      this.path.setLatLngs(coordinates)
      this.pathGeo.clearLayers()
    } else {
      this.path = L.polyline(coordinates)
      this.pathGeo = L.geoJSON().addTo(this.map)
    }
    this.pathGeo.addData(this.path.toGeoJSON())
    this.onResize()
  }

  convertForAntiMeridian (coords: LatLng[]): LatLng[] {
    let prevLng
    const newCoords = []
    for (let i = 0; i < coords.length; i++) {
      const coord = coords[i]
      if (prevLng === undefined) {
        prevLng = coord.lng
      }
      newCoords.push({
        lat: coord.lat,
        lng: coord.lng += coord.lng - prevLng > 180 ? -360 : prevLng - coord.lng > 180 ? 360 : 0
      })
    }
    return newCoords
  }

  antiMeridianLngFix (latLng: LatLng, prevLatLng: LatLng): LatLng {
    // needed when we cross the 180th meridian
    latLng.lng += latLng.lng - prevLatLng.lng > 180 ? -360 : prevLatLng.lng - latLng.lng > 180 ? 360 : 0
    return latLng
  }

  generateArcLine (start: LatLng, end: LatLng): LatLng[] {
    const circle = greatCircle(point([start.lng, start.lat]), point([end.lng, end.lat]), { npoints: 500 })
    if (circle.geometry.type === 'MultiLineString') {
      return circle.geometry.coordinates[0].concat(circle.geometry.coordinates[1]).map((point) => {
        return this.antiMeridianLngFix({ lat: point[1], lng: point[0] }, start)
      })
    } else {
      return circle.geometry.coordinates.map((point) => {
        return { lat: point[1], lng: point[0] }
      })
    }
  }

  onResize () {
    if (this.path) {
      const latLngs = this.path.getLatLngs()
      if (latLngs.length > 1) {
        // normal case, we have a proper path, so we can generate bounds
        const bounds = this.path.getBounds()
        this.map.fitBounds(bounds, { animate: false, padding: [50, 50] })
      } else if (latLngs.length === 1) {
        // special case, we don't have enough coordinates (diverted to departure airport?). Center on the first point
        const coord = latLngs[0] as LatLng
        this.map.setView(coord, 10)
      } else {
        // no points? Center on USA.
        this.map.setView({ lat: 37.0902, lng: -95.7129 }, 4)
      }
    }
  }
}
